import React, { useCallback, useContext, useMemo } from "react";
import { Switch, Typography } from "@mui/material";
import {
  FormattingRule,
  FormattingRuleType,
  ReorderIndexing,
  createFormattingRule,
  getFormattingRule,
  updateFormattingInstruction,
  PositionInstructions,
  startEndRowColToCellPosition,
} from "@coolset/cs-ui";
import { MainSurveyStore } from "modules/MainSurveyEditor/hooks/useMainSurvey";
import { useMainSurveyData } from "modules/MainSurveyEditor/hooks/useMainSurveyData";
import { SurveyBlocks } from "modules/MainSurveyEditor/surveyBlocks";
import { BlockOption, getBlockOption } from "modules/MainSurveyEditor/helpers/blocks";
import { BlockIcon } from "shared/components/BlockOption/BlockIcon";
import { CellBlockFormHeader } from "../Sections/CellBlockFormHeader";
import { mainSurveyClient } from "modules/MainSurveyEditor/client/mainSurveyClient";
import { TableCellSpan } from "./TableCellSpan";
import { LayoutContext } from "contexts/LayoutContext/LayoutContext";

export const TableCellInputs = (props: TableCellInputsProps) => {
  const {
    mainSurveyStore: { subBlockToEdit, setTableBlocks, tableBlocks, resetSubBlockToEdit },
    formattingInstructions,
    positionInstructions,
    onReorderBlock,
    onChange,
    isSavingPositioning,
    handleAfterDeleteBlock,
    setIsAwaitingBlockUpdate,
  } = props;

  const { genericError } = useContext(LayoutContext);

  const { surveyBlock, isLoading } = useMainSurveyData(subBlockToEdit?.parentSurveyId || "", subBlockToEdit?.id);

  const blockPosition = useMemo<string | undefined>(() => {
    if (!surveyBlock) return;

    return startEndRowColToCellPosition(positionInstructions[surveyBlock.id]);
  }, [surveyBlock, positionInstructions]);

  const blockOption = useMemo<BlockOption>(() => getBlockOption(subBlockToEdit?.type || ""), [subBlockToEdit]);

  const fieldId = useMemo<string | null | undefined>(() => {
    if (!surveyBlock) {
      return;
    }

    return (surveyBlock as SurveyBlocks.Block).contentObject.fieldId;
  }, [surveyBlock]);

  const cellRule = useMemo<FormattingRule | undefined>(
    () =>
      getFormattingRule(formattingInstructions, {
        ruleType: FormattingRuleType.Cell,
        position: blockPosition,
      }),
    [formattingInstructions, blockPosition]
  );

  // TODO - remove key if value is false for clean up
  const handleFormattingInstructionChange = useCallback(
    (changedValue: Record<string, string | boolean> = {}) => {
      if (!cellRule) {
        if (!blockPosition) return;
        const [newRules] = createFormattingRule(formattingInstructions, {
          ruleType: FormattingRuleType.Cell,
          position: blockPosition,
          ...changedValue,
        });
        onChange(newRules);
      } else {
        const [newRules] = updateFormattingInstruction(formattingInstructions, {
          ...cellRule,
          ...changedValue,
        });
        onChange(newRules);
      }
    },
    [cellRule, formattingInstructions, blockPosition, onChange]
  );

  const handleToggleHeaderRule = useCallback(
    (newValue: boolean) => {
      handleFormattingInstructionChange({ isHeader: newValue });
    },
    [handleFormattingInstructionChange]
  );

  const handleBlockTooltipRule = useCallback(
    (newValue: boolean) => {
      handleFormattingInstructionChange({ showBlockTooltip: newValue });
    },
    [handleFormattingInstructionChange]
  );

  const handleDatasetOrderChange = useCallback(
    (position: string) => {
      onReorderBlock({ [String(surveyBlock!.id)]: { oldPosition: blockPosition!, newPosition: position } });
    },
    [onReorderBlock, surveyBlock, blockPosition]
  );

  const handleClearCell = useCallback(async () => {
    if (!subBlockToEdit?.id) return;

    setIsAwaitingBlockUpdate(true);
    try {
      await mainSurveyClient.deleteSurveyBlock(subBlockToEdit.parentSurveyId, subBlockToEdit.id);

      const newTableBlocks = [...(tableBlocks || [])].filter((block) => block.id !== subBlockToEdit.id);
      setTableBlocks(newTableBlocks);
      resetSubBlockToEdit(newTableBlocks, positionInstructions);
      handleAfterDeleteBlock(String(subBlockToEdit.id));
    } catch (error) {
      console.error(error);
      setIsAwaitingBlockUpdate(false);
      genericError('Failed to delete cell. Please try again.');
    }
  }, [subBlockToEdit, tableBlocks, setTableBlocks, resetSubBlockToEdit, positionInstructions, handleAfterDeleteBlock, setIsAwaitingBlockUpdate]);

  return (
    <div>
      {!isLoading && (
        <>
          <CellBlockFormHeader onDeleteBlock={handleClearCell} />

          <div className="layout-block-form table-cell-inputs-container">
            <TableCellSpan
              onChange={handleDatasetOrderChange}
              blockPosition={blockPosition}
              isLoading={isLoading || isSavingPositioning}
            />

            <div className="_d-flex-ali-center-jc-sb-sm-gap">
              <Typography variant="h4" color="text.secondary">
                Set as header cell
              </Typography>
              <Switch
                className="module-field-switch"
                checked={!!cellRule?.isHeader}
                onChange={(event) => handleToggleHeaderRule(event.target.checked)}
              />
            </div>

            <div className="_d-flex-dir-col-ali-baseline-jc-sb-sm-gap">
              <Typography variant="h4" color="text.secondary">
                Value
              </Typography>
              <div className="block-option-container">
                <div className="_d-flex-ali-center-sm-gap">
                  <BlockIcon blockType={blockOption.blockType} />
                  <div>
                    <Typography variant="h4">{blockOption.name}</Typography>
                    {fieldId && <Typography sx={{ wordBreak: "break-all" }}>Field ID: {fieldId}</Typography>}
                  </div>
                </div>
              </div>
            </div>

            <div className="_d-flex-ali-center-jc-sb-sm-gap">
              <Typography variant="h4" color="text.secondary">
                Show tooltip from block
              </Typography>
              <Switch
                className="module-field-switch"
                checked={!!cellRule?.showBlockTooltip}
                onChange={(event) => handleBlockTooltipRule(event.target.checked)}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export interface TableCellInputsProps {
  mainSurveyStore: MainSurveyStore;
  formattingInstructions: FormattingRule[];
  positionInstructions: PositionInstructions;
  onReorderBlock: (reorderIndexing: ReorderIndexing) => void;
  onChange: (rules: FormattingRule[]) => void;
  isSavingPositioning: boolean;
  handleAfterDeleteBlock: (blockId: string) => void;
  setIsAwaitingBlockUpdate: (isAwaiting: boolean) => void;
}
